import React, { useState, useEffect } from "react";
import {
  User,
  Calendar,
  Package,
  CreditCard,
  Download,
  MapPin,
  Phone,
  Home,
  ChevronRight,
  Image as ImageIcon,
  FileText,
} from "lucide-react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";

const OrderDetails = () => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  let orderId = useParams().id;

  // Helper function to convert http URLs to https
const toHttps = (url) => {
  if (!url) return url;
  return /^http:\/\//i.test(url)
    ? url.replace(/^http:\/\//i, "https://")
    : url;
};

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const token = localStorage.getItem("Auth");
        if (!token) {
          setError("Authorization token not found");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `https://api.elza2.store/api/v1/order/${orderId}`,
          {
            headers: {
              Authorization: token,
            },
          }
        );

        setOrderData(response.data.order);
        setLoading(false);
      } catch (err) {
        setError(err.message || "An unknown error occurred");
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

const handleDownload = (url, fileName) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

  const handleBackToOrders = () => {
    const page = searchParams.get("page") || "1";
    navigate(`/myorders`, {
      search: `?page=${page}`,
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading order details: {error}</div>;
  if (!orderData) return <div>No order data found</div>;

const handleSaveReceipt = () => {
  const input = document.getElementById("order-receipt");

  html2canvas(input, {
    scale: 2,
    useCORS: true,
    windowWidth: input.scrollWidth,
    windowHeight: input.scrollHeight,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // First page
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    // Add extra pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`Order_${orderId}_Receipt.pdf`);
  });
};

  return (
    <div className="bg-gray-50 min-h-screen p-8">
      <div className="max-w-4xl mx-auto mb-4">
        <nav className="flex items-center space-x-2 text-gray-500">
          <button
            onClick={handleBackToOrders}
            className="hover:text-blue-600 flex items-center"
          >
            <Home className="mr-2" size={16} />
            Orders
          </button>
          <ChevronRight size={16} />
          <span className="text-gray-800 font-semibold">Order Details</span>
        </nav>
      </div>
      <div
        id="order-receipt"
        className="max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-white">Order Details</h1>
            <button
              onClick={handleSaveReceipt}
              className="flex items-center bg-white text-blue-600 px-4 py-2 rounded-full hover:bg-gray-100 transition-all duration-300 shadow-md"
            >
              <Download className="mr-2" /> Save Receipt
            </button>
          </div>
        </div>

        {/* Order Overview */}
        <div className="grid md:grid-cols-2 gap-6 p-6 bg-gray-50">
          {[
            {
              icon: <User className="text-blue-500" />,
              label: "Customer",
              value:
                `${orderData?.shippingAddress?.firstName || ""} ${
                  orderData?.shippingAddress?.lastName || ""
                }` ||
                orderData?.user?.name ||
                "N/A",
            },
            {
              icon: <Calendar className="text-green-500" />,
              label: "Order Date",
              value: orderData?.createdAt
                ? format(new Date(orderData.createdAt), "MMMM dd, yyyy")
                : "N/A",
            },
            {
              icon: <Package className="text-purple-500" />,
              label: "Order Status",
              value: orderData?.status?.phase || "N/A",
              type: "status",
            },
            {
              icon: <CreditCard className="text-indigo-500" />,
              label: "Payment Status",
              value: orderData?.status?.isPaid ? "Paid" : "Cash On Delivery",
              type: "status",
              color: orderData?.status?.isPaid ? "green" : "red",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-4 shadow-md flex items-center space-x-4"
            >
              <div className="p-3 bg-gray-100 rounded-full">{item.icon}</div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {item.label}
                </p>
                {item.type === "status" ? (
                  <span
                    className={`
                      px-3 py-1 rounded-full text-sm font-medium 
                      ${
                        item.color === "green"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }
                    `}
                  >
                    {item.value}
                  </span>
                ) : (
                  <p className="font-semibold text-gray-800">{item.value}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Products */}
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Products</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    User Image
                  </th>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="p-3 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Custom Product Images
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderData?.orderItems?.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="p-3">
                      <div className="flex items-center space-x-4">
                        {/* Check if the product is a custom product or regular product */}
                        {item.type === "Custom Product" ? (
                          <div>
                            {/* Custom Product Image */}
                            {item.customProduct?.images?.[0] && (
                              <img
                                src={toHttps(item.customProduct.images[0])}
                                alt={item.customProduct.name}
                                className="w-16 h-16 object-cover rounded-lg shadow-md"
                              />
                            )}
                            <div>
                              <p className="font-semibold text-gray-700">
                                Custom - {item.customProduct?.category}
                              </p>
                              <p className="text-sm text-gray-500">
                                ({item?.size})
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {item.product?.image && (
                              <img
                                src={toHttps(item.product.image)}
                                alt={item.product.name}
                                className="w-16 h-16 object-cover rounded-lg shadow-md"
                              />
                            )}
                            <div>
                              <p className="font-semibold text-gray-800">
                                {item.product?.name}
                              </p>
                              <p className="text-sm text-gray-600">
                                Size: {item.size}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">
                      EGP {item.price?.toLocaleString()}
                    </td>
                    <td className="p-3 text-gray-600">{item.quantity}</td>
                    <td className="p-3 font-semibold text-gray-800">
                      EGP {item.totalPrice?.toLocaleString()}
                    </td>
                    <td className="p-3">
                      {item.userImage ? (
                        <button
                          onClick={() =>
                            handleDownload(
                              toHttps(item.userImage),
                              `user-image-${orderId}-${index}.jpg`
                            )
                          }
                          className="flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <ImageIcon className="w-5 h-5 mr-1" />
                          Download
                        </button>
                      ) : (
                        <span className="text-gray-400">No image</span>
                      )}
                    </td>
                    <td className="p-3">
                      {item.customProduct?.description ? (
                        <p className="text-sm text-gray-600 mt-2">
                          {item.customProduct.description}
                        </p>
                      ) : null}
                      {item.userNote && item.userNote.length > 0 ? (
                        <div className="text-sm text-gray-600">
                          {item.userNote.map((note, i) => (
                            <p key={i}>{note}</p>
                          ))}
                        </div>
                      ) : null}
                      {!item.customProduct?.description &&
                        (!item.userNote || item.userNote.length === 0) && (
                          <span className="text-gray-400">No notes</span>
                        )}
                    </td>
                    <td className="p-3">
                      {item.type === "Custom Product" &&
                      item.customProduct?.images?.length > 0 ? (
                        item.customProduct.images.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={() =>
                              handleDownload(
                                toHttps(img),
                                `custom-product-image-${orderId}-${index}-${idx}.jpg`
                              )
                            }
                            className="flex items-center text-blue-600 hover:text-blue-800"
                          >
                            <ImageIcon className="w-5 h-5 mr-1" />
                            Download Custom Image
                          </button>
                        ))
                      ) : (
                        <span className="text-gray-400">No images</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Address and Summary */}
        <div className="grid md:grid-cols-2 gap-6 p-6 bg-gray-50">
          {/* Shipping Address */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Shipping Address
            </h2>
            <div className="bg-white rounded-xl p-4 shadow-md space-y-2">
              <div className="flex items-start space-x-3">
                <MapPin className="text-blue-500 mt-1 flex-shrink-0" />
                <div>
                  <p>{orderData?.shippingAddress?.streetAddress || "N/A"}</p>
                  <p>{orderData?.shippingAddress?.city || "N/A"}</p>
                  <p>{orderData?.shippingAddress?.zipCode || "N/A"}</p>
                </div>
              </div>
              <div className="flex flex-col space-y-2 mt-2">
                <div className="flex items-center space-x-3">
                  <Phone className="text-green-500 flex-shrink-0" />
                  <p>{orderData?.shippingAddress?.phone || "N/A"}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="text-green-500 flex-shrink-0" />
                  <p>{orderData?.shippingAddress?.recoveryNumber || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Order Summary
            </h2>
            <div className="bg-white rounded-xl p-4 shadow-md space-y-3">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">
                  EGP {orderData?.pricing?.subtotal?.toLocaleString() || "N/A"}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Shipping Cost</span>
                <span className="font-semibold">
                  EGP{" "}
                  {orderData?.pricing?.shippingCost?.toLocaleString() || "N/A"}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Discount</span>
                <span className="font-semibold">
                  EGP{" "}
                  {(
                    Number(orderData?.pricing?.subtotal) +
                    Number(orderData?.pricing?.shippingCost) -
                    Number(orderData?.pricing?.totalAfterDiscount)
                  ).toLocaleString() || "0"}
                </span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="text-lg font-bold text-gray-800">Total</span>
                <span className="text-xl font-bold text-blue-600">
                  EGP{" "}
                  {orderData?.pricing?.totalAfterDiscount?.toLocaleString() ||
                    "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
